import type { Metric, SessionStats } from './contracts'

/**
 * Скоринг. Реализация docs/form-index-math.md.
 * Всё здесь - чистые функции. Никакого React, никакого хранилища.
 */

// --- константы модели ---

/** Размер скользящего окна для персональной базы */
export const WINDOW = 20
/** До этого числа сессий индекс не показываем */
export const MIN_SAMPLES = 5
/** Коэффициент сглаживания EWMA */
export const ALPHA = 0.3
/** Пол стандартного отклонения: защита от нулевой дисперсии */
export const SIGMA_REL_FLOOR = 0.05
export const SIGMA_ABS_FLOOR = 1e-6
/** Ограничение z, чтобы выбросы не ломали шкалу */
export const Z_CLAMP = 4
/** Параметры шкалы: 100 = обычная форма, 15 = одно отклонение */
export const SCALE_CENTER = 100
export const SCALE_SPREAD = 15

// --- базовая статистика ---

export function mean(xs: readonly number[]): number {
  if (xs.length === 0) return 0
  let sum = 0
  for (const x of xs) sum += x
  return sum / xs.length
}

/** Выборочное стандартное отклонение (делитель n-1). */
export function sd(xs: readonly number[]): number {
  if (xs.length < 2) return 0
  const m = mean(xs)
  let acc = 0
  for (const x of xs) acc += (x - m) ** 2
  return Math.sqrt(acc / (xs.length - 1))
}

/**
 * Отклонение с полом. Без него три одинаковых результата подряд
 * дают sigma = 0 и деление на ноль.
 */
export function safeSd(xs: readonly number[]): number {
  const raw = sd(xs)
  const floor = Math.max(SIGMA_REL_FLOOR * Math.abs(mean(xs)), SIGMA_ABS_FLOOR)
  return Math.max(raw, floor)
}

export function clamp(x: number, min: number, max: number): number {
  return Math.min(Math.max(x, min), max)
}

/**
 * Экспоненциальное сглаживание с корректировкой смещения.
 *
 * Наивная версия (s = x[0], дальше рекурсия) на коротких рядах
 * переоценивает ПЕРВОЕ значение: при 4 точках старое весит больше
 * свежего. У нас окно 5-20 сессий, так что это ломало бы смысл
 * индекса - он цеплялся бы за самую давнюю тренировку.
 *
 * Решение то же, что в оптимизаторе Adam: копим с нуля и делим
 * на нормализатор 1 - (1-alpha)^n.
 */
export function ewma(xs: readonly number[], alpha: number = ALPHA): number {
  if (xs.length === 0) return 0
  let s = 0
  for (const x of xs) s = alpha * x + (1 - alpha) * s
  const bias = 1 - (1 - alpha) ** xs.length
  return bias === 0 ? s : s / bias
}

// --- показатель сессии ---

/**
 * Приводит историю сессий к ряду сопоставимых чисел.
 *
 * span           - берём объём напрямую.
 * speed-accuracy - BIS = z(PC) - z(RT), нормы персональные.
 *
 * Почему BIS, а не IES/RCS: IES и RCS взвешивают скорость и точность
 * неравномерно и сами чувствительны к компромиссу скорость-точность.
 * См. docs/form-index-math.md, раздел 2.1.
 */
export function rawSeries(history: readonly SessionStats[], metric: Metric): number[] {
  if (history.length === 0) return []
  if (metric === 'span') return history.map((h) => h.span)

  const pcs = history.map((h) => h.pc)
  const rts = history.map((h) => h.rtMs)
  const muP = mean(pcs)
  const sdP = safeSd(pcs)
  const muR = mean(rts)
  const sdR = safeSd(rts)

  return history.map((h) => (h.pc - muP) / sdP - (h.rtMs - muR) / sdR)
}

// --- индекс ---

/**
 * Счёт может быть в трёх состояниях:
 *   collecting без значения - данных нет совсем
 *   collecting со значением - есть предварительная оценка
 *   ready - набрана персональная база, счёт настоящий
 */
export type HoneScore =
  | {
      readonly status: 'collecting'
      readonly sessionsLeft: number
      /** Грубая оценка по абсолютному ориентиру; null, если данных нет */
      readonly provisional: number | null
    }
  | { readonly status: 'ready'; readonly value: number }

/** Значение счёта, каким его показывать. null - показывать нечего. */
export function scoreValue(score: HoneScore): number | null {
  return score.status === 'ready' ? score.value : score.provisional
}

export interface DifficultyRange {
  readonly min: number
  readonly max: number
  readonly initial: number
}

/**
 * Предварительная оценка по достигнутому уровню.
 *
 * Персональный baseline требует нескольких заходов, но человек не должен
 * видеть прочерк после первой же тренировки. Здесь ориентир абсолютный:
 * стартовый уровень игры считается серединой шкалы, и отклонение от него
 * переводится в те же 100 плюс-минус 15.
 *
 * Это грубо и сознательно помечается как предварительное: оценка говорит
 * лишь «вы выше или ниже типичного старта», а не сравнивает вас с собой.
 */
export function provisionalScore(level: number, range: DifficultyRange): number {
  const spread = Math.max((range.max - range.min) / 4, 1)
  const z = clamp((level - range.initial) / spread, -3, 3)
  return Math.round(SCALE_CENTER + SCALE_SPREAD * z)
}

/**
 * Предварительная оценка по всем заходам, а не по последнему.
 *
 * Уровни сглаживаются тем же EWMA: свежие заходы весят больше, но
 * один неудачный день не обнуляет картину. По одному последнему заходу
 * оценка прыгала бы вместе со случайной неудачей.
 */
export function provisionalFrom(
  history: readonly SessionStats[],
  range: DifficultyRange,
): number | null {
  if (history.length === 0) return null
  const levels = history.slice(-WINDOW).map((h) => h.level)
  return provisionalScore(ewma(levels), range)
}

/**
 * Hone Score по одной игре.
 * Сравнение с собственной базой пользователя, не с популяцией.
 */
export function gameScore(
  history: readonly SessionStats[],
  metric: Metric,
  range?: DifficultyRange,
): HoneScore {
  if (history.length < MIN_SAMPLES) {
    return {
      status: 'collecting',
      sessionsLeft: MIN_SAMPLES - history.length,
      provisional: range ? provisionalFrom(history, range) : null,
    }
  }

  const window = history.slice(-WINDOW)
  const raw = rawSeries(window, metric)
  const mu = mean(raw)
  const sigma = safeSd(raw)
  const zs = raw.map((x) => clamp((x - mu) / sigma, -Z_CLAMP, Z_CLAMP))

  return {
    status: 'ready',
    value: Math.round(SCALE_CENTER + SCALE_SPREAD * ewma(zs)),
  }
}

// --- агрегация ---

export interface GameContribution {
  readonly score: number
  readonly sessions: number
  readonly lastPlayedAt: number
  /** Оценка предварительная - в итог идёт, но не делает счёт настоящим */
  readonly provisional?: boolean
}

/** Период полураспада веса игры, дни */
export const RECENCY_HALFLIFE_DAYS = 14
/** Сколько сессий нужно для полного доверия к игре */
export const FULL_CONFIDENCE_SESSIONS = 5

const DAY_MS = 86_400_000

export function weightOf(c: GameContribution, now: number): number {
  const days = Math.max(0, (now - c.lastPlayedAt) / DAY_MS)
  const recency = 0.5 ** (days / RECENCY_HALFLIFE_DAYS)
  const confidence = Math.min(1, c.sessions / FULL_CONFIDENCE_SESSIONS)
  return recency * confidence
}

/**
 * Индекс дисциплины: взвешенное среднее по играм.
 * Игра, в которую давно не играли, почти не влияет.
 */
export function disciplineScore(
  contributions: readonly GameContribution[],
  now: number = Date.now(),
): HoneScore {
  const usable = contributions.filter((c) => weightOf(c, now) > 0)
  if (usable.length === 0) {
    return { status: 'collecting', sessionsLeft: MIN_SAMPLES, provisional: null }
  }

  let num = 0
  let den = 0
  for (const c of usable) {
    const w = weightOf(c, now)
    num += w * c.score
    den += w
  }
  if (den === 0) return { status: 'collecting', sessionsLeft: MIN_SAMPLES, provisional: null }

  const value = Math.round(num / den)
  const allProvisional = usable.every((c) => c.provisional === true)

  if (allProvisional) {
    return { status: 'collecting', sessionsLeft: MIN_SAMPLES, provisional: value }
  }
  return { status: 'ready', value }
}

/**
 * Итоговый Hone Score из счётов всех дисциплин.
 *
 * Каждая дисциплина считается ОДИН раз, независимо от того, сколько
 * в ней игр. Взвешивать по числу игр было бы двойным учётом
 * надёжности: внутри дисциплины `disciplineScore` уже ослабляет
 * игры с малым числом заходов и давние. Иначе восприятие с одной
 * игрой оказалось бы структурно менее важным, даже когда измерено
 * не хуже памяти с двумя.
 *
 * Если хотя бы одна дисциплина набрала базу - счёт настоящий:
 * лучше показать часть картины, чем ничего.
 */
export function honeScore(scores: readonly HoneScore[]): HoneScore {
  const ready = scores.filter(
    (s): s is { status: 'ready'; value: number } => s.status === 'ready',
  )
  if (ready.length > 0) {
    return { status: 'ready', value: Math.round(mean(ready.map((s) => s.value))) }
  }

  const provisionals = scores.map(scoreValue).filter((v): v is number => v !== null)
  const left = scores.map((s) => (s.status === 'collecting' ? s.sessionsLeft : 0))

  return {
    status: 'collecting',
    sessionsLeft: left.length > 0 ? Math.max(...left) : MIN_SAMPLES,
    provisional: provisionals.length > 0 ? Math.round(mean(provisionals)) : null,
  }
}

// --- сборка статистики раунда ---

export interface RoundLog {
  readonly correct: boolean
  readonly accuracy: number
  readonly rtMs: number
  readonly level: number
}

/** Свернуть раунды одного захода в SessionStats. */
export function summarize(rounds: readonly RoundLog[], at: number = Date.now()): SessionStats {
  if (rounds.length === 0) {
    return { pc: 0, rtMs: 0, span: 0, level: 0, at }
  }

  const correctRounds = rounds.filter((r) => r.correct)
  // время считаем только по правильным ответам - так принято в психометрике:
  // время ошибочного ответа не отражает скорость обработки
  const rtSource = correctRounds.length > 0 ? correctRounds : rounds
  const passedLevels = correctRounds.map((r) => r.level)
  const lastLevel = rounds[rounds.length - 1]?.level ?? 0

  return {
    pc: Math.round((correctRounds.length / rounds.length) * 1000) / 1000,
    // округляем: CloudStorage ограничен 4096 байтами на значение,
    // а дробные хвосты вида 1234.5678901 едят его зря
    rtMs: Math.round(mean(rtSource.map((r) => r.rtMs))),
    span: passedLevels.length > 0 ? Math.max(...passedLevels) : 0,
    level: lastLevel,
    at,
  }
}
